import { useId, useState } from "react";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import {
  AutocompleteOption,
  type AutocompleteOptionType,
  createFilterAutocompleteOption,
} from "../../../shared/Autocomplete";
import { saveScimPermissionMutationOptions, type ScimPermission } from "../../../api/scimPermissions";
import type { ScimAdGroup } from "../../../api/scimAdGroups";
import { DynamicShared } from "../../../dynamicShared";
import { ErrorMessage } from "../../../dynamicForm/inlineForm/ErrorMessage";
import { type DisplayedRole, roleTexts, useRoleInfoKey } from "../Roles";

type ScimMappingInputProps = {
  tenantId: string;
  role: DisplayedRole;
  adGroups: Array<ScimAdGroup>;
  permissions: Array<ScimPermission>;
};

export const ScimMappingInput = ({ tenantId, role, adGroups, permissions }: ScimMappingInputProps) => {
  const permission = permissions.find(p => p.tenantId === tenantId && p.role === role);
  const remountKey = permission ? `${permission.id}:${permission.adGroupIds.join(",")}` : `empty:${role}`;
  return (
    <ScimMappingInputForm
      key={remountKey}
      tenantId={tenantId}
      role={role}
      adGroups={adGroups}
      permission={permission}
    />
  );
};

type ScimMappingInputFormProps = {
  tenantId: string;
  role: DisplayedRole;
  adGroups: Array<ScimAdGroup>;
  permission: ScimPermission | undefined;
};

const ScimMappingInputForm = ({ tenantId, role, adGroups, permission }: ScimMappingInputFormProps) => {
  const { t } = useTranslation();
  const roleInfoKey = useRoleInfoKey();
  const labelId = useId();

  const adGroupOptions: Array<AutocompleteOptionType> = adGroups.map(group => ({
    value: group.id,
    label: group.name,
    description: group.externalId,
  }));

  const saveScimPermission = useMutation(saveScimPermissionMutationOptions());
  const [draftIds, setDraftIds] = useState<Array<string>>(permission?.adGroupIds ?? []);

  const draftOptions = draftIds
    .map(id => adGroupOptions.find(option => option.value === id))
    .filter((option): option is AutocompleteOptionType => Boolean(option));

  const save = (nextIds: Array<string>) => {
    const currentAdGroupIds = permission?.adGroupIds ?? [];
    if (areIdSetsEqual(nextIds, currentAdGroupIds)) return;

    saveScimPermission.reset();
    saveScimPermission.mutate(
      { permission, tenantId, role, adGroupIds: nextIds },
      { onError: () => setDraftIds(currentAdGroupIds) }
    );
  };

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack>
        <Typography id={labelId} variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t(roleTexts[role].labelKey)}
        </Typography>
        <Typography variant="body2" sx={theme => ({ color: theme.palette.text.tertiary })}>
          {t(roleInfoKey(role))}
        </Typography>
      </Stack>
      <Stack sx={{ maxWidth: 500 }}>
        <Autocomplete<AutocompleteOptionType, true>
          multiple
          size="small"
          clearIcon={null}
          openOnFocus
          disableCloseOnSelect
          clearOnBlur
          disabled={saveScimPermission.isPending}
          options={adGroupOptions}
          value={draftOptions}
          onChange={(_, next, reason) => {
            const nextIds = next.map(option => option.value);
            setDraftIds(nextIds);
            if (reason === "removeOption") save(nextIds);
          }}
          onBlur={() => {
            if (saveScimPermission.isPending) return;
            save(draftIds);
          }}
          filterOptions={createFilterAutocompleteOption}
          isOptionEqualToValue={(option, selected) => option.value === selected.value}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return <AutocompleteOption key={option.value} dense {...optionProps} option={option} />;
          }}
          renderValue={(selected, getItemProps) =>
            selected.map((option, index) => {
              const { key, onDelete, tabIndex } = getItemProps({ index });
              return (
                <DynamicShared.Chip
                  key={key}
                  label={option.label}
                  onDelete={onDelete}
                  tabIndex={tabIndex}
                  variant="choice"
                  inputState="editing"
                />
              );
            })
          }
          noOptionsText={t("ScimMapping.NoAdGroupsAvailable")}
          sx={{ [`& .${autocompleteClasses.inputRoot}`]: { gap: 0.5 } }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={draftOptions.length === 0 ? t("ScimMapping.AdGroupPlaceholder") : undefined}
              slotProps={{
                ...params.slotProps,

                htmlInput: {
                  ...params.slotProps.htmlInput,
                  "aria-labelledby": labelId,
                },
              }}
            />
          )}
        />
        {saveScimPermission.isError && <ErrorMessage />}
      </Stack>
    </Stack>
  );
};

const areIdSetsEqual = (a: ReadonlyArray<string>, b: ReadonlyArray<string>) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every(id => set.has(id));
};
